// Simple, privacy-first analytics system
// All data stays local unless user explicitly opts in to sharing

export interface AnalyticsEvent {
  timestamp: number;
  sessionId: string;
  type: 'navigation' | 'interaction' | 'recording' | 'playback' | 'error' | 'feature';
  action: string;
  page?: string;
  metadata?: Record<string, any>;
}

export interface SessionSummary {
  sessionId: string;
  startTime: number;
  endTime: number;
  events: number;
  pages: string[];
  recordings: number;
  playbacks: number;
  errors: number;
}

class Analytics {
  private events: AnalyticsEvent[] = [];
  private sessionId: string;
  private sessionStart: number;
  private enabled: boolean;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.sessionStart = Date.now();
    this.enabled = this.checkOptIn();
    this.loadEvents();
    
    // Log session start
    this.track('navigation', 'session_start', {
      page: window.location.pathname,
      referrer: document.referrer,
      screen: `${window.innerWidth}x${window.innerHeight}`,
      device: this.getDeviceType()
    });

    // Setup page visibility tracking
    this.setupVisibilityTracking();
    
    // Periodic save to localStorage
    setInterval(() => this.saveEvents(), 30000);
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private checkOptIn(): boolean {
    // Check if user has opted in to analytics
    return localStorage.getItem('analytics_opt_in') === 'true';
  }

  private getDeviceType(): string {
    if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
      return 'mobile';
    }
    if (window.innerWidth < 768) {
      return 'mobile';
    }
    return 'desktop';
  }

  private setupVisibilityTracking() {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.track('navigation', 'page_blur', { 
          duration: Date.now() - this.sessionStart 
        });
      } else {
        this.track('navigation', 'page_focus');
      }
    });

    window.addEventListener('beforeunload', () => {
      this.track('navigation', 'session_end', {
        duration: Date.now() - this.sessionStart,
        totalEvents: this.events.length
      });
      this.saveEvents();
    });
  }

  // Main tracking method
  track(type: AnalyticsEvent['type'], action: string, metadata?: Record<string, any>) {
    if (!this.enabled && type !== 'error') return;

    const event: AnalyticsEvent = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      type,
      action,
      page: window.location.pathname,
      metadata
    };

    this.events.push(event);
    
    // Log to console in dev mode
    if (import.meta.env.DEV) {
      console.log('📊 Analytics:', event);
    }

    // Save critical events immediately
    if (type === 'error' || action === 'recording_complete') {
      this.saveEvents();
    }
  }

  // Specific tracking methods
  trackNavigation(to: string, from?: string) {
    this.track('navigation', 'route_change', { to, from });
  }

  trackRecording(duration: number, transcriptLength?: number) {
    this.track('recording', 'recording_complete', {
      duration,
      transcriptLength,
      quality: duration > 5 ? 'good' : 'short'
    });
  }

  trackPlayback(recordingId: string, completion: number) {
    this.track('playback', completion > 0.9 ? 'playback_complete' : 'playback_partial', {
      recordingId,
      completion,
      listenTime: completion * 100
    });
  }

  trackInteraction(element: string, value?: any) {
    this.track('interaction', element, { value });
  }

  trackFeature(feature: string, action: string, metadata?: Record<string, any>) {
    this.track('feature', `${feature}_${action}`, metadata);
  }

  trackError(error: Error, context?: string) {
    this.track('error', error.name, {
      message: error.message,
      stack: error.stack,
      context
    });
  }

  // Save events to localStorage
  private saveEvents() {
    if (!this.events.length) return;

    const stored = localStorage.getItem('analytics_events');
    const existing = stored ? JSON.parse(stored) : [];
    
    // Keep only last 1000 events
    const combined = [...existing, ...this.events].slice(-1000);
    localStorage.setItem('analytics_events', JSON.stringify(combined));
    
    // Clear current batch
    this.events = [];
  }

  private loadEvents() {
    const stored = localStorage.getItem('analytics_events');
    if (stored) {
      this.events = JSON.parse(stored);
    }
  }

  // Get analytics summary
  getSummary(): SessionSummary {
    const allEvents = [...this.events];
    const stored = localStorage.getItem('analytics_events');
    if (stored) {
      allEvents.push(...JSON.parse(stored));
    }

    const sessionEvents = allEvents.filter(e => e.sessionId === this.sessionId);
    const pages = [...new Set(sessionEvents
      .map(e => e.page)
      .filter((page): page is string => typeof page === 'string'))];

    return {
      sessionId: this.sessionId,
      startTime: this.sessionStart,
      endTime: Date.now(),
      events: sessionEvents.length,
      pages,
      recordings: sessionEvents.filter(e => e.type === 'recording').length,
      playbacks: sessionEvents.filter(e => e.type === 'playback').length,
      errors: sessionEvents.filter(e => e.type === 'error').length
    };
  }

  // Get insights from events
  getInsights() {
    const stored = localStorage.getItem('analytics_events');
    if (!stored) return null;

    const allEvents = JSON.parse(stored) as AnalyticsEvent[];
    
    // Most visited pages
    const pageCounts = allEvents.reduce((acc, e) => {
      if (e.page) {
        acc[e.page] = (acc[e.page] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    // Common user flows
    const flows: string[][] = [];
    let currentFlow: string[] = [];
    allEvents.forEach(e => {
      if (e.type === 'navigation') {
        if (e.action === 'session_start') {
          currentFlow = [e.page || '/'];
        } else if (e.action === 'route_change' && e.metadata?.to) {
          currentFlow.push(e.metadata.to);
        } else if (e.action === 'session_end' && currentFlow.length > 1) {
          flows.push([...currentFlow]);
          currentFlow = [];
        }
      }
    });

    // Feature usage
    const features = allEvents
      .filter(e => e.type === 'feature')
      .reduce((acc, e) => {
        acc[e.action] = (acc[e.action] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

    // Error patterns
    const errors = allEvents
      .filter(e => e.type === 'error')
      .map(e => ({
        name: e.action,
        message: e.metadata?.message,
        count: 1
      }));

    return {
      totalEvents: allEvents.length,
      uniqueSessions: new Set(allEvents.map(e => e.sessionId)).size,
      mostVisitedPages: Object.entries(pageCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5),
      commonFlows: flows.slice(0, 5),
      featureUsage: Object.entries(features)
        .sort(([, a], [, b]) => b - a),
      recentErrors: errors.slice(-5),
      averageSessionLength: this.calculateAvgSessionLength(allEvents)
    };
  }

  private calculateAvgSessionLength(events: AnalyticsEvent[]): number {
    const sessions = new Map<string, { start: number; end: number }>();
    
    events.forEach(e => {
      const id = e.sessionId;
      if (!sessions.has(id)) {
        sessions.set(id, { start: e.timestamp, end: e.timestamp });
      } else {
        const session = sessions.get(id)!;
        session.end = Math.max(session.end, e.timestamp);
      }
    });

    const durations = Array.from(sessions.values()).map(s => s.end - s.start);
    return durations.length > 0 
      ? durations.reduce((a, b) => a + b, 0) / durations.length / 1000 / 60 
      : 0; // in minutes
  }

  // Enable/disable analytics
  setEnabled(enabled: boolean) {
    this.enabled = enabled;
    localStorage.setItem('analytics_opt_in', enabled ? 'true' : 'false');
    
    if (enabled) {
      this.track('feature', 'analytics_enabled');
    }
  }

  // Clear all analytics data
  clearData() {
    this.events = [];
    localStorage.removeItem('analytics_events');
    this.track('feature', 'analytics_cleared');
  }

  // Export data for user
  exportData(): string {
    const stored = localStorage.getItem('analytics_events');
    const allEvents = stored ? JSON.parse(stored) : [];
    
    return JSON.stringify({
      exportDate: new Date().toISOString(),
      sessionId: this.sessionId,
      events: allEvents,
      summary: this.getSummary(),
      insights: this.getInsights()
    }, null, 2);
  }
}

// Singleton instance
export const analytics = new Analytics();

// React hook for analytics
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export function useAnalytics() {
  const location = useLocation();

  useEffect(() => {
    analytics.trackNavigation(location.pathname);
  }, [location]);

  return analytics;
}